/* PlanIterationsStack.jsx — doctor's stacked treatment-plan history.
 *
 * Each plan iteration (the plan as it was sent to the doctor at one point
 * in time) is rendered as its own card. Newest on top. The active iteration
 * is expanded and carries the Approve / Request-revision actions; older
 * iterations are collapsed and pin the doctor's own revision comment at
 * the top — so the conversation reads cleanly when scrolling.
 *
 * Data sources:
 *   GET /cases/{id}/plan-versions   → ordered snapshots [{version, reason, plan, created_at, snapshotted_by_*}]
 *   case.timeline[]                 → contains the doctor's revision notes (action="revision_requested" / "case_revision_requested")
 *
 * The "iteration" model we surface to the doctor:
 *   • Each `sent_for_approval` snapshot opens a new iteration.
 *   • A subsequent `revision_requested` snapshot closes that iteration with
 *     verdict "revised" + the doctor's note.
 *   • An `approved` snapshot closes the *last* iteration with verdict "approved".
 *   • If case.status is "revision_requested" but no new plan has been sent
 *     yet, we still render a placeholder card "Planner is preparing your
 *     revised plan…" so the doctor sees forward motion.
 *
 * Designed to surface ALL the existing chrome (PlanPanel, planner notes,
 * Approve/Revise footer) without changing their internals.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Sparkle, CheckCircle, ArrowUUpLeft, CaretDown, CaretUp,
    NotePencil, GitDiff, FileArrowUp, Warning, ClockClockwise,
} from "@phosphor-icons/react";
import ApprovePayDialog from "./ApprovePayDialog";
import { Button } from "@/components/ui/button";
import { statusLabel, isPostApproval } from "@/lib/caseStatus";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import PlanPanel from "@/components/case/PlanPanel";
import PlanSummary from "@/components/case/PlanSummary";
import DoctorWearScheduleReview from "@/components/case/DoctorWearScheduleReview";
import SectionCard from "@/components/case/SectionCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
};

/* Walk the versions array into a list of "iterations" the doctor mentally
   maps to: "the plan as sent → the doctor's verdict on it". */
function buildIterations(versions, caseStatus, timeline = [], casePlan = null) {
    // Sort by version ascending; we'll group then flip to newest-first.
    const sorted = [...(versions || [])].sort((a, b) => (a.version || 0) - (b.version || 0));
    const revisionNotes = (timeline || [])
        .filter((e) => e.action === "revision_requested" || e.action === "case_revision_requested")
        .map((e) => ({ note: e.note || "", at: e.created_at, by: e.actor_name || e.actor_role }));

    let revIdx = 0;
    const iterations = [];
    for (const snap of sorted) {
        if (snap.reason === "sent_for_approval" || snap.reason === "submitted_to_qa" || snap.reason === "planner_resubmit") {
            // New iteration begins. `planner_resubmit` is the same
            // doctor-facing event as `sent_for_approval` — it just
            // happens after a revision round (iter98.99bl).
            iterations.push({
                iterationNumber: iterations.length + 1,
                sentAt: snap.created_at,
                sentByName: snap.snapshotted_by_name,
                plan: snap.plan || {},
                version: snap.version,
                verdict: "pending",
                doctorNote: null,
                verdictAt: null,
            });
        } else if (snap.reason === "revision_requested" && iterations.length) {
            const last = iterations[iterations.length - 1];
            last.verdict = "revised";
            last.verdictAt = snap.created_at;
            last.doctorNote = revisionNotes[revIdx]?.note || null;
            revIdx += 1;
        } else if (snap.reason === "approved" && iterations.length) {
            const last = iterations[iterations.length - 1];
            last.verdict = "approved";
            last.verdictAt = snap.created_at;
        }
    }

    // Fallback: legacy/demo cases that have a `plan` payload on the case but
    // no snapshots in `case_plan_versions` (the snapshot pipeline is newer
    // than these cases). Synthesise a single virtual iteration so the
    // doctor still sees the actual plan rather than an empty placeholder.
    if (!iterations.length && casePlan
        && ["sent_for_approval", "ready_for_review", "approved",
            "revision_requested", "in_production", "assigned_to_production",
            "shipped", "in_treatment", "treatment_started", "completed"].includes(caseStatus)) {
        iterations.push({
            iterationNumber: 1,
            sentAt: casePlan.updated_at || casePlan.created_at,
            sentByName: casePlan.updated_by_name,
            plan: casePlan,
            version: casePlan.version || 1,
            verdict: "pending",
            doctorNote: null,
            verdictAt: null,
        });
    }

    // If the case is currently in revision_requested but planner hasn't sent
    // a new plan yet, the last iteration is "revised" and the doctor is waiting.
    const waitingOnPlanner = caseStatus === "revision_requested" || (caseStatus === "in_planning" && iterations.length > 0);

    // Latest iteration's verdict reflects the live state: if status is
    // sent_for_approval / ready_for_review, mark it pending. If approved,
    // mark approved. The snapshot stream usually agrees, but the live case
    // state is the source of truth for the *latest* card.
    if (iterations.length) {
        const last = iterations[iterations.length - 1];
        if (caseStatus === "approved") last.verdict = "approved";
        else if (caseStatus === "sent_for_approval" || caseStatus === "ready_for_review") last.verdict = "pending";
        else if (caseStatus === "revision_requested") last.verdict = "revised";
    }

    return { iterations: iterations.reverse(), waitingOnPlanner };
}

/* ── Iteration card ── */
function IterationCard({ iteration, caseId, caseObj, caseFiles, setupFiles, isLatest, isFirstIteration, caseStatus,
                         canApprove, onApprove, onChange }) {
    const [expanded, setExpanded] = useState(isLatest);
    const verdict = iteration.verdict;

    const verdictBadge = verdict === "approved"
        ? { label: "Approved by you", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle }
        : verdict === "revised"
            ? { label: "You requested a revision", cls: "bg-rose-100 text-rose-800 border-rose-200", Icon: ArrowUUpLeft }
            : { label: "Awaiting your review", cls: "bg-cyan-100 text-cyan-800 border-cyan-200", Icon: Sparkle };

    // Active iteration only when this is the latest AND status is decision-ready.
    const showDecisionFooter = isLatest && verdict === "pending"
        && (caseStatus === "sent_for_approval" || caseStatus === "ready_for_review");

    // Right-side caption — only show post-decision states. Pre-decision the
    // verdict badge already conveys "Awaiting your review", so a second caption
    // saying the same thing is noise. Keep approval/revision/planning states
    // since those carry forward-motion info the badge doesn't capture.
    const rightCaption = (() => {
        if (!isLatest) return null;
        if (caseStatus === "approved") return `Approved on ${fmtDate(iteration.verdictAt || iteration.sentAt)}`;
        if (caseStatus === "revision_requested") return "Awaiting revised plan";
        if (caseStatus === "in_planning") return "Planning team working on next plan";
        return null;
    })();

    const chromeBg = isLatest
        ? "border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white"
        : "border-border bg-card";

    // Card subtitle copy — only the first iteration carries a friendly intro.
    // Revised iterations skip it (the eyebrow "REVISED PLAN READY" + the
    // doctor's own revision note pinned above already set the context).
    const subtitle = isLatest && verdict === "pending" && isFirstIteration
        ? "Your planning team has prepared this treatment plan — please review the per-tooth setup, attachments, wear schedule, and protocol below."
        : null;

    return (
        <div
            className={`rounded-2xl border overflow-hidden relative shadow-[0_1px_0_rgba(0,0,0,0.02)] ${chromeBg}`}
            data-testid={`plan-iteration-card-v${iteration.iterationNumber}`}
        >

            {/* Header */}
            <div className={`px-5 sm:px-6 pt-5 pb-3 ${isLatest ? "bg-cyan-50/60 border-b border-cyan-200/70" : ""}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className={`flex items-center gap-3 min-w-0 flex-1 text-left -m-1 p-1 rounded-md ${isLatest ? "hover:bg-cyan-100/60" : "hover:bg-muted/60"}`}
                        aria-expanded={expanded}
                        data-testid={`plan-iteration-toggle-v${iteration.iterationNumber}`}
                    >
                        <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 text-white ${
                            isLatest ? "bg-primary" : verdict === "approved" ? "bg-emerald-600"
                            : verdict === "revised" ? "bg-rose-600" : "bg-zinc-500"
                        }`}>
                            <Sparkle size={16} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <div className={`text-[10px] font-bold tracking-[0.18em] uppercase ${isLatest ? "text-primary" : "text-muted-foreground"}`}>
                                {isLatest && verdict === "pending"
                                    ? (isFirstIteration ? "Treatment plan ready" : "Revised plan ready")
                                    : `Treatment plan v${iteration.iterationNumber}`}
                            </div>
                            <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                                <span>Sent {fmtDate(iteration.sentAt)}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${verdictBadge.cls}`}>
                                    <verdictBadge.Icon size={10} weight="fill" /> {verdictBadge.label}
                                </span>
                            </div>
                            {subtitle && (
                                <div className="text-xs text-muted-foreground mt-1.5 max-w-2xl leading-snug">{subtitle}</div>
                            )}
                        </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                        {rightCaption && (
                            <div className="text-[11px] text-muted-foreground tabular-nums hidden sm:block" data-testid="plan-iteration-right-caption">
                                {rightCaption}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className={`w-7 h-7 rounded-full grid place-items-center ${isLatest ? "bg-white border border-cyan-200 text-primary hover:text-cyan-900" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
                            aria-label={expanded ? "Minimise" : "Expand"}
                            data-testid={`plan-iteration-toggle-icon-v${iteration.iterationNumber}`}
                        >
                            {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Doctor's revision note pinned at top for revised iterations */}
            {verdict === "revised" && iteration.doctorNote && (
                <div className="mx-5 sm:mx-6 mt-3 rounded-xl border border-rose-200/70 bg-rose-50/60 p-3" data-testid={`plan-iteration-doctor-note-v${iteration.iterationNumber}`}>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700 mb-1 flex items-center gap-1.5">
                        <ArrowUUpLeft size={12} weight="fill" /> Your revision comment · {fmtDate(iteration.verdictAt)}
                    </div>
                    <div className="text-sm text-rose-900/90 leading-snug whitespace-pre-wrap">{iteration.doctorNote}</div>
                </div>
            )}

            {/* Approved attribution */}
            {verdict === "approved" && (
                <div className="mx-5 sm:mx-6 mt-3 rounded-xl border border-emerald-200/70 bg-emerald-50/60 p-3 text-sm text-emerald-900 flex items-center gap-2">
                    <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                    You approved this plan on {fmtDate(iteration.verdictAt)}.
                </div>
            )}

            {/* Body */}
            {expanded && (
                <div className="px-5 sm:px-6 py-5 space-y-4">
                    {/* Planner notes (top of body — most conversational element)
                        iter98.99de — Wrapped in SectionCard so the rhythm
                        matches the Submission tab brief. */}
                    {iteration.plan?.planner_notes && (
                        <SectionCard
                            icon={NotePencil}
                            label="Note from your planning team"
                            testid={`plan-iteration-planner-notes-v${iteration.iterationNumber}`}
                        >
                            <div className="text-sm text-foreground/85 leading-snug whitespace-pre-wrap">{iteration.plan.planner_notes}</div>
                        </SectionCard>
                    )}

                    {/* Reuse the existing PlanPanel body — it knows how to
                        render plan.external_link, video, IPR map, etc. We
                        pass embedded=true so it skips its own card chrome
                        (the iteration card already provides the frame). */}
                    <PlanPanel
                        caseId={caseId}
                        plan={iteration.plan}
                        status={caseStatus}
                        caseFiles={caseFiles || []}
                        embedded
                    />

                    {/* iter97.8 — Per-tooth chart + wear schedule live INSIDE
                        the iteration card so the doctor reads everything as
                        one cohesive plan. We only render these on the latest
                        iteration because they reflect the case's CURRENT plan
                        (c.plan / c.prescription), not the snapshotted iteration.
                        Older iterations stay focused on their snapshotted
                        external link + planner notes — historical, read-only. */}
                    {isLatest && caseObj && (
                        <>
                            <PlanSummary
                                prescription={caseObj.prescription}
                                plan={caseObj.plan}
                                viewer="doctor"
                            />
                            <DoctorWearScheduleReview c={caseObj} onChange={onChange} />
                        </>
                    )}

                    {/* Latest iteration also surfaces setup-file attachments
                        iter98.99de — Wrapped in SectionCard for visual
                        consistency with the rest of the case detail page. */}
                    {isLatest && setupFiles?.length > 0 && (
                        <SectionCard
                            icon={FileArrowUp}
                            label={`Attached deliverables (${setupFiles.length})`}
                            testid={`plan-iteration-setup-files-v${iteration.iterationNumber}`}
                        >
                            <div className="space-y-1.5">
                                {setupFiles.map((f) => (
                                    <a
                                        key={f.id}
                                        href={`${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-sm text-primary hover:underline flex items-center gap-1.5"
                                        data-testid={`setup-file-${f.id}`}
                                    >
                                        <FileArrowUp size={14} /> {f.name}
                                    </a>
                                ))}
                            </div>
                        </SectionCard>
                    )}

                    {/* Decision footer — Approve / Request revision — appears
                        only on the active, pending iteration. */}
                    {showDecisionFooter && (
                        <PlanDecisionFooterInline
                            caseId={caseId}
                            canApprove={canApprove}
                            onApprove={onApprove}
                            onChange={onChange}
                            plan={iteration.plan}
                            setupFiles={isLatest ? (setupFiles || []) : []}
                            planningMode={caseObj?.planning_mode || "coe"}
                            pricing={caseObj?.pricing || {}}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

/* ── Decision footer extracted as inline so the stack stays self-contained ── */
function PlanDecisionFooterInline({ caseId, canApprove, onApprove, onChange, plan, setupFiles = [], planningMode = "coe", pricing = {} }) {
    // iter98.86 — the OLD inline revision dialog flow has been replaced
    // with the dedicated full-page side-by-side `/request-revision`
    // route so the doctor can compare the planner's plan against their
    // own proposed changes in a structured, per-section editor.
    const navigate = useNavigate();
    const [planRead, setPlanRead] = useState(false);
    // iter98.fix-tier-ownership / Phase 5b — for CoE cases where the
    // planner picked the tier, the doctor sees a commit-summary modal
    // before the approval actually fires. Self-Plan cases retain the
    // direct-approve UX (the doctor already committed to the tier at
    // Step 6).
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [committing, setCommitting] = useState(false);
    /* iter98.99cp — Widened the gate. The rich Payments & Offers modal
       should fire for ANY CoE-planned case at approval time so the
       doctor sees a full financial summary before committing. The
       original gate (only when planner picked the tier) was too narrow
       since the doctor might still want to confirm at approval even
       when they picked the tier at submission. Self-Plan cases (no
       planner) still go direct since the doctor already saw the
       breakdown on Step 6. */
    const needsCommitDialog = planningMode === "coe" && !!pricing?.tier_id;

    const handleApproveClick = () => {
        if (needsCommitDialog) {
            setPayDialogOpen(true);
        } else {
            onApprove(true);
        }
    };

    const handleConfirmCommit = async () => {
        setCommitting(true);
        try {
            await onApprove(true);
            setPayDialogOpen(false);
        } finally {
            setCommitting(false);
        }
    };

    if (!canApprove) {
        return (
            <div className="border-t border-cyan-200/70 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-4 mt-3 text-sm text-muted-foreground italic" data-testid="plan-decision-footer-readonly">
                Only the treating doctor can approve or request a revision on this plan.
            </div>
        );
    }

    return (
        <div
            id="doctor-decision-anchor"
            className="border-t border-cyan-200/70 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-4 mt-3 scroll-mt-24"
            data-testid="plan-decision-footer"
        >
            {/* iter98.99cw — Doctor-side decision footer now mirrors the
                planner's "Take your call" hero (PlannerCaseDetail.jsx):
                Decision eyebrow + headline + subtitle, then a 2-col
                grid (Path A · Approve / Path B · Revise) separated by an
                OR pill. Same visual language as the planner's accept-vs-
                clarify surface so both sides feel like one product when
                they meet at the approval moment. */}
            <div
                className="relative rounded-2xl bg-card border border-border overflow-hidden shadow-[0_1px_0_rgba(0,0,0,0.02)]"
                data-testid="doctor-decision-hero"
            >
                {/* HERO RIBBON */}
                <div className="px-5 sm:px-7 pt-5 pb-3 bg-gradient-to-br from-secondary/40 via-card to-card border-b border-border">
                    <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-muted-foreground">
                        Decision
                    </span>
                    <h2
                        className="font-display text-2xl sm:text-[26px] font-bold tracking-tight text-foreground mt-1.5"
                        data-testid="doctor-decision-title"
                    >
                        Take your call
                    </h2>
                    <p className="text-[13px] text-muted-foreground mt-1 max-w-2xl">
                        You&apos;ve reviewed the planner&apos;s proposal. Either approve the plan and proceed to payment, or pause and ask the planner for specific changes.
                    </p>
                </div>

                {/* TWO-BRANCH BODY */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-stretch">

                    {/* PATH A — Approve (emerald) */}
                    <div className="p-5 sm:p-6 flex flex-col" data-testid="doctor-decision-approve-card">
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 ring-1 ring-emerald-200">
                                <CheckCircle size={18} weight="fill" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-emerald-700">
                                    Path A · Approve
                                </div>
                                <h3 className="font-display text-base font-bold text-foreground mt-0.5">
                                    Approve the plan
                                </h3>
                            </div>
                        </div>
                        <p className="text-[12.5px] text-muted-foreground leading-relaxed mt-3">
                            Lock this plan in and proceed to payment. Manufacturing kicks off as soon as your planning team finalises the spec.
                        </p>
                        <div className="mt-auto pt-4 space-y-3">
                            <label
                                className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2.5"
                                data-testid={`approve-attestation-${caseId}`}
                            >
                                <Checkbox
                                    checked={planRead}
                                    onCheckedChange={(v) => setPlanRead(v === true)}
                                    data-testid={`plan-read-checkbox-${caseId}`}
                                    className="mt-0.5 shrink-0"
                                />
                                <span className="text-[12px] text-foreground/85 leading-snug">
                                    <span className="font-semibold">I have read the plan</span> — I confirm I&apos;ve reviewed the setup, IPR, attachments, auxiliaries, retention and protocol above, and that this plan is clinically appropriate for the patient.
                                </span>
                            </label>
                            <Button
                                onClick={handleApproveClick}
                                disabled={!planRead}
                                size="lg"
                                className="w-full rounded-full text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`approve-case-${caseId}`}
                                title={planRead ? undefined : "Tick the read-confirmation above to enable approval"}
                            >
                                <CheckCircle size={15} className="mr-2" weight="fill" />
                                {needsCommitDialog ? "Approve & proceed to payment" : "Approve plan"}
                            </Button>
                        </div>
                    </div>

                    {/* OR DIVIDER — vertical on desktop, horizontal on mobile */}
                    <div className="flex items-center justify-center lg:flex-col py-0 lg:py-6 lg:px-0">
                        <div className="hidden lg:block w-px flex-1 bg-border" aria-hidden />
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground/60 px-3 py-1 bg-card border border-border rounded-full">
                            or
                        </span>
                        <div className="hidden lg:block w-px flex-1 bg-border" aria-hidden />
                        <div className="lg:hidden flex-1 h-px bg-border" aria-hidden />
                    </div>

                    {/* PATH B — Request revision (rose) */}
                    <div className="p-5 sm:p-6 flex flex-col" data-testid="doctor-decision-revise-card">
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-rose-100 text-rose-700 grid place-items-center shrink-0 ring-1 ring-rose-200">
                                <ArrowUUpLeft size={18} weight="fill" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-rose-700">
                                    Path B · Revise
                                </div>
                                <h3 className="font-display text-base font-bold text-foreground mt-0.5">
                                    Need revisions?
                                </h3>
                            </div>
                        </div>
                        <p className="text-[12.5px] text-muted-foreground leading-relaxed mt-3">
                            Open the <span className="font-semibold text-foreground">per-section revision editor</span> and point out exactly what needs to change — section by section. The case pauses until the planner ships an updated draft.
                        </p>
                        <div className="mt-auto pt-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate(`/doctor/cases/${caseId}/request-revision`)}
                                size="lg"
                                className="w-full rounded-full text-sm font-semibold text-rose-700 border-rose-300 hover:bg-rose-50 hover:text-rose-800"
                                data-testid={`request-revision-${caseId}`}
                            >
                                <ArrowUUpLeft size={15} className="mr-2" weight="fill" />
                                Open revision editor
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <ApprovePayDialog
                open={payDialogOpen}
                onOpenChange={setPayDialogOpen}
                caseId={caseId}
                pricing={pricing}
                busy={committing}
                onConfirm={handleConfirmCommit}
                onChange={onChange}
            />
        </div>
    );
}

/* ── Empty / waiting placeholders ── */
function PlanInPlanningCard({ caseStatus, planner }) {
    const TITLE = caseStatus === "revision_requested"
        ? "Your planning team is preparing the revised plan"
        : caseStatus === "in_planning"
            ? "Your planning team is preparing the treatment plan"
            : caseStatus === "assigned_to_planner"
                ? "Planner assigned — waiting for them to start"
                : "Your Invisique™ planning team is reviewing your submission";
    const SUB = caseStatus === "revision_requested"
        ? "You'll see the new plan here as soon as it's sent."
        : "We'll notify you the moment the treatment plan is ready for your review.";
    return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-3" data-testid="plan-waiting-card">
            <ClockClockwise size={20} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
            <div>
                <div className="text-sm font-semibold text-amber-900">{TITLE}</div>
                <div className="text-xs text-amber-800/80 mt-0.5">{SUB}</div>
                {planner && (
                    <div className="text-[11px] text-muted-foreground mt-2">Assigned planner: {planner}</div>
                )}
            </div>
        </div>
    );
}

/* iter101.1 — Plan-empty guard for the approval window.
   Fires when case.status ∈ {sent_for_approval, ready_for_review} but no
   plan-versions snapshot exists AND no tier has been committed (i.e. the
   planner routed the case to the doctor without filling any of the plan).
   Rather than leave the doctor stuck without any CTA, we surface a clear
   Request Revision escape hatch so the case can be bounced back to the
   planner with a note. Approve is intentionally hidden — no tier means no
   pricing, so the doctor cannot commit to production. */
function PlanEmptyInApprovalCard({ caseId, canApprove }) {
    const navigate = useNavigate();
    return (
        <div
            className="rounded-2xl border border-rose-200 bg-rose-50/60 p-5 flex flex-col sm:flex-row items-start gap-4"
            data-testid="plan-empty-in-approval-card"
        >
            <Warning size={22} weight="fill" className="text-rose-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-rose-900">
                    This plan appears incomplete
                </div>
                <div className="text-xs text-rose-800/80 mt-1 leading-relaxed">
                    Your Invisique™ planning team routed this case to you for approval but the treatment plan is empty — no aligner count, no wear schedule, no treatment tier. You can bounce it back with a note so the planner can fill in the details before you approve.
                </div>
                {canApprove && (
                    <div className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/doctor/cases/${caseId}/request-revision`)}
                            size="sm"
                            className="rounded-full text-xs font-semibold text-rose-700 border-rose-300 hover:bg-rose-100 hover:text-rose-800"
                            data-testid={`plan-empty-request-revision-${caseId}`}
                        >
                            <ArrowUUpLeft size={13} className="mr-2" weight="fill" />
                            Request revision from planner
                        </Button>
                    </div>
                )}
                {!canApprove && (
                    <div className="text-[11px] text-muted-foreground mt-3 italic">
                        Only the treating doctor can request a revision on this plan.
                    </div>
                )}
            </div>
        </div>
    );
}

/* iter101.5 — Read-only fallback for post-approval cases whose original
   plan-version snapshot is missing (a residue of the pre-iter101.1 QA-
   approve bug where transitions to `sent_for_approval` didn't write a
   snapshot). Instead of showing the misleading "planning team is
   reviewing your submission" placeholder — which is what production
   currently displays for an approved case — we render the live `c.plan`
   as a read-only "Approved plan" card so the doctor still sees the
   details of what they signed off on. */
function PlanApprovedReadOnlyCard({ c }) {
    const label = statusLabel(c.status, "doctor");
    return (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5" data-testid="plan-approved-readonly-card">
            <div className="flex items-start gap-3 mb-3">
                <CheckCircle size={22} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-emerald-900">
                        Plan approved · {label}
                    </div>
                    <div className="text-xs text-emerald-800/80 mt-0.5">
                        You signed off on this plan. Nothing more to do here — the plan below is what your Invisique™ team is delivering.
                    </div>
                </div>
            </div>
            <div className="rounded-xl bg-white border border-emerald-100 p-4">
                <PlanPanel caseId={c.id} plan={c.plan || {}} status={c.status} caseFiles={c.files || []} embedded />
            </div>
        </div>
    );
}

/* ── Main stack ── */
export default function PlanIterationsStack({ c, setupFiles, canApprove, onApprove, onChange }) {
    const [versions, setVersions] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/cases/${c.id}/plan-versions`)
            .then((r) => { if (mounted) setVersions(r.data || []); })
            .catch(() => { if (mounted) setVersions([]); })
            .finally(() => { if (mounted) setLoading(false); });
    }, [c.id, c.status, c.plan?.updated_at]);

    const { iterations, waitingOnPlanner } = useMemo(
        () => buildIterations(versions, c.status, c.timeline || [], c.plan || null),
        [versions, c.status, c.timeline, c.plan],
    );

    if (loading) return <div className="text-muted-foreground text-xs py-8 text-center">Loading plan history…</div>;

    /* iter101.1 — Hero-owns-review must fire BEFORE the empty-iterations
       branch. If the case is a CoE plan sitting in the doctor's approval
       queue with a tier already committed by the planner, `PlanApprovalHero`
       renders the full plan summary above and this component owns ONLY the
       decision footer (Approve / Request Revision). Prior to this fix the
       "!iterations.length" check short-circuited and rendered a misleading
       "planning team is reviewing your submission" empty state whenever
       the QA-approve path bypassed the plan-version snapshot — the doctor
       saw the plan but no way to approve or request a revision. */
    const inApprovalWindow = c.status === "sent_for_approval" || c.status === "ready_for_review";
    const heroOwnsReview = (c.planning_mode || "coe") === "coe"
        && inApprovalWindow
        && !!(c.pricing?.tier_id);

    if (heroOwnsReview) {
        // Prefer the latest snapshotted iteration; fall back to the live
        // `c.plan` object when no snapshot exists (QA-approve legacy cases).
        const latest = iterations[0];
        const planForFooter = latest?.plan || c.plan || {};
        return (
            <div className="space-y-4" data-testid="plan-iterations-stack">
                <div className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white px-5 sm:px-6 py-5">
                    <PlanDecisionFooterInline
                        caseId={c.id}
                        canApprove={canApprove}
                        onApprove={onApprove}
                        onChange={onChange}
                        plan={planForFooter}
                        setupFiles={setupFiles || []}
                        planningMode={c.planning_mode || "coe"}
                        pricing={c.pricing || {}}
                    />
                </div>
            </div>
        );
    }

    /* No iteration yet AND we're not in the hero-owns-review lane. This
       branch has three sub-cases the copy MUST distinguish (previously
       they all fell through to the misleading "planning team is
       reviewing your submission" placeholder):

       (a) Post-approval case (doctor already signed off but the
           original plan-versions snapshot is missing — happens for
           cases that transitioned via the pre-iter101.1 QA-approve
           path). Render the approved plan itself in read-only mode
           so the doctor still sees the details of what they signed.

       (b) Approval window with empty plan (planner routed to doctor
           without filling anything) — show the "incomplete plan"
           warning with Request Revision escape hatch.

       (c) Truly still with the planner (draft, submitted, assigned,
           in_planning, revision_requested, etc.) — show the
           "planning team is preparing" waiting card. */
    if (!iterations.length) {
        if (isPostApproval(c.status)) {
            return <PlanApprovedReadOnlyCard c={c} />;
        }
        if (inApprovalWindow) {
            return <PlanEmptyInApprovalCard caseId={c.id} canApprove={canApprove} />;
        }
        return <PlanInPlanningCard caseStatus={c.status} planner={c.planner_name} />;
    }

    return (
        <div className="space-y-4" data-testid="plan-iterations-stack">
            {/* If status is revision_requested and the latest iteration is
                already verdicted "revised", show a waiting placeholder at top
                so the doctor sees "next plan is being prepared". */}
            {waitingOnPlanner && c.status === "revision_requested" && (
                <PlanInPlanningCard caseStatus={c.status} planner={c.planner_name} />
            )}

            {iterations.map((it, idx) => (
                <IterationCard
                    key={`${it.version}-${it.iterationNumber}`}
                    iteration={it}
                    caseId={c.id}
                    caseObj={c}
                    caseFiles={c.files || []}
                    setupFiles={idx === 0 ? setupFiles : []}
                    isLatest={idx === 0}
                    isFirstIteration={it.iterationNumber === 1}
                    caseStatus={c.status}
                    canApprove={canApprove}
                    onApprove={onApprove}
                    onChange={onChange}
                />
            ))}

            {iterations.length > 1 && (
                <div className="text-[11px] text-muted-foreground text-center pt-1">
                    Showing all {iterations.length} plan iterations · newest first ·
                    {" "}use the <span className="font-semibold">Compare plans</span> tab to see what changed
                </div>
            )}
        </div>
    );
}
